import React, { useMemo, useState } from "react";
import { Outlet, NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Wallet,
  FileBarChart,
  Settings as SettingsIcon,
  Menu,
  X,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  ChevronsLeft,
  User as UserIcon,
  PhoneCall,
  MessageSquare,
  Droplets,
  AlertCircle,
  PieChart,
  CreditCard,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { useSearch } from "../context/SearchContext";
import DropdownMenu from "./DropdownMenu";
import ConfirmDialog from "./ConfirmDialog";
import Tooltip from "./Tooltip";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import ImpersonationBanner from "./ImpersonationBanner";
import {
  computeTenantStatus,
  currentMonth,
  tenantBalance,
  formatKES,
} from "../utils/format";
import { readKey, writeKey } from "../utils/storage";

const navItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/properties", label: "Properties", icon: Building2 },
  { to: "/app/tenants", label: "Tenants", icon: Users },
  { to: "/app/rent", label: "Rent Status", icon: Wallet },
  { to: "/app/utilities", label: "Utilities", icon: Droplets },
  { to: "/app/calls", label: "AI Calls", icon: PhoneCall },
  { to: "/app/sms", label: "SMS Reminders", icon: MessageSquare },
  { to: "/app/reports", label: "View Statement", icon: FileBarChart },
  { to: "/app/billing", label: "Billing", icon: CreditCard },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
];

const pageTitles = {
  "/app/dashboard": "Dashboard",
  "/app/properties": "Properties",
  "/app/tenants": "Tenants",
  "/app/rent": "Rent Status",
  "/app/utilities": "Utilities",
  "/app/calls": "AI Calls",
  "/app/sms": "SMS Reminders",
  "/app/reports": "View Statement",
  "/app/billing": "Billing",
  "/app/settings": "Settings",
};

export default function Layout() {
  const { user, signOut } = useAuth();
  const { tenants, payments } = useData();
  const { query, setQuery, placeholder: searchPlaceholder, enabled: searchEnabled } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readKey("sidebarCollapsed", false));
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const title =
    pageTitles[location.pathname] ||
    (location.pathname.startsWith("/app/properties/") ? "Property" : "Paysafi");

  const initials = (user?.fullName || user?.email || "U")
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  // Notifications: overdue, partial, pending tenants for the current month.
  const alerts = useMemo(() => {
    const month = currentMonth();
    const active = tenants.filter((t) => t.status === "ACTIVE");
    return active
      .map((t) => ({
        tenant: t,
        status: computeTenantStatus(t, payments, month),
        balance: tenantBalance(t, payments, month),
      }))
      .filter((a) => a.status === "OVERDUE" || a.status === "PARTIAL")
      .sort((a, b) => (a.status === "OVERDUE" ? -1 : 1));
  }, [tenants, payments]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writeKey("sidebarCollapsed", next);
      return next;
    });
  };

  const sidebarTransform = collapsed ? "-translate-x-full" : "translate-x-0";
  const contentOffset = collapsed ? "" : "lg:pl-64";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Sidebar - desktop */}
      <aside
        aria-hidden={collapsed}
        className={`fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-gray-200 bg-white transition-transform duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-900 lg:block ${sidebarTransform} ${collapsed ? "pointer-events-none" : ""}`}
      >
        <SidebarContent
          onNavigate={() => setMobileOpen(false)}
          onToggleCollapsed={toggleCollapsed}
        />
      </aside>

      {/* Sidebar - mobile overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${mobileOpen ? "" : "pointer-events-none"}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!mobileOpen}
      >
        <div
          onClick={() => setMobileOpen(false)}
          className={`absolute inset-0 bg-gray-900/40 transition-opacity duration-300 ease-in-out ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
        />
        <aside
          className={`absolute inset-y-0 left-0 w-64 border-r border-gray-200 bg-white transition-transform duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-900 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <SidebarContent
            onNavigate={() => setMobileOpen(false)}
            onToggleCollapsed={() => setMobileOpen(false)}
            isMobile
          />
        </aside>
      </div>

      {/* Topbar */}
      <div className={`transition-[padding] duration-300 ease-in-out ${contentOffset}`}>
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <Tooltip label="Open menu">
                <button
                  className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </Tooltip>
              {collapsed && (
                <Tooltip label="Show sidebar">
                  <button
                    className="hidden rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:inline-flex dark:text-gray-300 dark:hover:bg-gray-800"
                    onClick={toggleCollapsed}
                    aria-label="Show sidebar"
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                </Tooltip>
              )}
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h1>
            </div>

            <div className="hidden flex-1 max-w-md md:block">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchEnabled ? searchPlaceholder : "Search not available on this page"}
                  disabled={!searchEnabled}
                  className="input-base pl-9 disabled:cursor-not-allowed disabled:bg-gray-50"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <ThemeToggle />
                <NotificationBell alerts={alerts} onNavigate={navigate} />
              </div>

              <DropdownMenu
                align="right"
                trigger={(toggle, isOpen) => (
                  <button
                    onClick={toggle}
                    className={`flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 ${
                      isOpen ? "bg-gray-50 dark:bg-gray-700" : ""
                    }`}
                    aria-label="Account menu"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
                      {initials || "U"}
                    </span>
                    <div className="hidden text-left sm:block">
                      <div className="text-xs font-semibold text-gray-900 leading-tight dark:text-white">
                        {user?.fullName || "User"}
                      </div>
                      <div className="text-[11px] text-gray-500 leading-tight dark:text-gray-400">
                        {user?.email}
                      </div>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-500 transition-transform duration-150 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                )}
                items={[
                  { label: "Profile", icon: UserIcon, onClick: () => navigate("/app/settings") },
                  { label: "Settings", icon: SettingsIcon, onClick: () => navigate("/app/settings") },
                  { divider: true },
                  {
                    label: "Sign out",
                    icon: LogOut,
                    tone: "danger",
                    onClick: () => setConfirmSignOut(true),
                  },
                ]}
                menuClassName="min-w-[14rem]"
              />
            </div>
          </div>
        </header>

        <ImpersonationBanner />

        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      <ConfirmDialog
        open={confirmSignOut}
        onClose={() => !signingOut && setConfirmSignOut(false)}
        title="Sign out?"
        description="You'll need to sign back in to access your dashboard."
        confirmLabel={signingOut ? "Signing out…" : "Sign out"}
        cancelLabel="Stay signed in"
        tone="danger"
        loading={signingOut}
        onConfirm={async () => {
          setSigningOut(true);
          try {
            await signOut();
          } finally {
            setSigningOut(false);
            setConfirmSignOut(false);
          }
        }}
      />
    </div>
  );
}

function SidebarContent({ onNavigate, onToggleCollapsed, isMobile }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center justify-between border-b border-gray-200 px-5 dark:border-gray-700">
        <Link to="/?view=landing" className="flex min-w-0 cursor-pointer items-center gap-2">
          <Logo size={32} className="flex-none" />
          <span className="truncate text-base font-semibold tracking-tight text-gray-900 dark:text-white">
            Paysafi
          </span>
        </Link>
        {isMobile ? (
          <Tooltip label="Close">
            <button
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
              onClick={onNavigate}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </Tooltip>
        ) : (
          <Tooltip label="Hide sidebar" side="right">
            <button
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={onToggleCollapsed}
              aria-label="Hide sidebar"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
      </div>
      <nav className="flex-1 space-y-1 p-3">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                isActive
                  ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white",
              ].join(" ")
            }
          >
            <Icon className="h-4 w-4 flex-none" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-gray-200 p-4 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-500">
        Paysafi v1.0
      </div>
    </div>
  );
}

function NotificationBell({ alerts, onNavigate }) {
  const count = alerts.length;
  return (
    <DropdownMenu
      align="right"
      menuClassName="min-w-[16rem] sm:min-w-[20rem]"
      trigger={(toggle, isOpen) => (
        <Tooltip label={count ? `${count} alert${count === 1 ? "" : "s"}` : "No alerts"}>
          <button
            onClick={toggle}
            className={`relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 ${
              isOpen ? "bg-gray-100" : ""
            }`}
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
                {count > 9 ? "9+" : count}
              </span>
            )}
          </button>
        </Tooltip>
      )}
      items={
        alerts.length === 0
          ? [{ label: "You're all caught up ✓", disabled: true }]
          : [
              ...alerts.slice(0, 6).map((a) => ({
                label: `${a.tenant.name} · House ${a.tenant.houseNumber}`,
                icon: a.status === "OVERDUE" ? AlertCircle : PieChart,
                hint: formatKES(a.balance),
                onClick: () => onNavigate("/app/rent"),
              })),
              { divider: true },
              {
                label: "View all in Rent Status",
                icon: Wallet,
                onClick: () => onNavigate("/app/rent"),
              },
            ]
      }
    />
  );
}
